import React, { useEffect } from 'react';
import { convertDigitToProperFormat } from 'utils/number';
import { getClassObjectBaseOnPeriodType, getPeriodValues } from './DatePeriod';
import { IPeriodCounts } from '../UniqueQuestion';
import Icon from 'components/Icon';
import {
  Periods,
  Wrapper,
  ChangeDate,
  PeriodLabel,
  RangeHeader,
  PeriodTypeContainer,
} from './styles';
import messages from '../../Messages';
import SelectWithPopover from '../../../../../../Select/SelectWithPopover';
import { red } from '../../../../../../../utils/style/colors';
import { ISurveyItem, TimeScale } from 'store/survey/surveyTypes';
import { useTranslation } from 'react-i18next';
import i18n, { languageDirection } from '../../../../../../../i18n';
import { ILocal } from '../../../../../../Chart/BaseDatePicker';
import {
  isCurrentDateInTheRangeOfOffset,
  SINGLE_DAY_BASE_ON_TIME_STAMP,
} from '../../../SurveyResponseViewer/ResponseItem';

export interface IOffset {
  start: number;
  end: number;
}

interface IPeriodTypeSelectorProps {
  setNoValidPeriodFound: (set?: boolean) => void;
  loading: boolean;
  hideCounts: boolean;
  changeDate: (
    addOrSubtract: 'add' | 'subtract' | 'reset'
  ) => (value?: number) => void;
  periodType: TimeScale;
  currentDate: Date;
  periodCounts: IPeriodCounts;
  selectedPeriod: number;
  onChangeValues: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showResponseForm: boolean;
  responseEffectiveDate?: number;
  surveyItem?: ISurveyItem;
  originalPeriodType: TimeScale;
  setPeriodType: (periodType: TimeScale) => void;
  offset?: IOffset | null;
  local?: string;
}

const PeriodTypeSelector: React.FC<IPeriodTypeSelectorProps> = ({
  changeDate,
  hideCounts,
  periodType,
  currentDate,
  originalPeriodType,
  periodCounts,
  selectedPeriod,
  onChangeValues,
  showResponseForm,
  surveyItem,
  setPeriodType,
  offset,
  local,
  setNoValidPeriodFound,
}) => {
  const periodTypes = [
    { value: 'day', label: i18n.t('daily', { ns: 'survey' }) },
    { value: 'week', label: i18n.t('weekly', { ns: 'survey' }) },
    { value: 'month', label: i18n.t('monthly', { ns: 'survey' }) },
    { value: 'season', label: i18n.t('season', { ns: 'survey' }) },
    { value: 'twoSeason', label: i18n.t('twoSeason', { ns: 'survey' }) },
    { value: 'year', label: i18n.t('yearly', { ns: 'survey' }) },
  ];

  const {
    t,
    i18n: { language },
  } = useTranslation('survey');

  const allPeriodTimes = getPeriodValues(
    periodType,
    currentDate,
    local as ILocal
  );
  const periodMomentValues = periodCounts && Object.keys(periodCounts);
  const periodValues = Object.values(allPeriodTimes);
  const middleKey = Math.floor(periodValues.length / 2);

  const { superName, nextTitle, prevTitle } = periodValues[middleKey];

  const hasSelectedPeriod = showResponseForm && !!selectedPeriod;

  useEffect(() => {
    if (!offset) return;
    if (selectedPeriod) {
      const period = getClassObjectBaseOnPeriodType(
        periodType,
        Number(selectedPeriod)
      );
      if (!isCurrentDateInTheRangeOfOffset(period.start, period.end, offset)) {
        onChangeValues({
          target: {
            name: periodType,
            value: '',
            checked: false,
            // dataset comes from UniqueBox & PeriodTypeSelector's inputs
            dataset: { label: '', order: '-1' },
          },
        } as any);
      }
    } else {
      const periodByStart = getClassObjectBaseOnPeriodType(
        periodType,
        new Date().valueOf() - offset.start * SINGLE_DAY_BASE_ON_TIME_STAMP
      );
      const periodByEnd = getClassObjectBaseOnPeriodType(
        periodType,
        new Date().valueOf() - offset.end * SINGLE_DAY_BASE_ON_TIME_STAMP
      );

      if (periodByEnd.start > periodByStart.start) {
        setNoValidPeriodFound(true);
        if (currentDate.valueOf() !== periodByEnd.start)
          changeDate('reset')(periodByEnd.start);
      } else {
        const viewingStartDate = Math.min(
          ...Object.values(allPeriodTimes).map((it) => it.start)
        );
        const viewingEndDate = Math.max(
          ...Object.values(allPeriodTimes).map((it) => it.end)
        );
        if (periodByEnd.start > viewingEndDate)
          changeDate('reset')(periodByEnd.start);
        else if (periodByStart.end < viewingStartDate)
          changeDate('reset')(periodByStart.end);
        if (periodByStart.start === periodByEnd.start) {
          if (
            !isCurrentDateInTheRangeOfOffset(
              periodByStart.start,
              periodByStart.end,
              offset
            )
          ) {
            setNoValidPeriodFound(true);
          } else {
            setNoValidPeriodFound(false);
            const suggestedDate = Math.floor(
              (periodByStart.start + periodByStart.end) / 2
            );
            onChangeValues({
              target: {
                name: periodType,
                value: suggestedDate,
                checked: true,
                // dataset comes from UniqueBox & PeriodTypeSelector's inputs
                dataset: { label: '', order: '-1' },
              },
            } as any);
          }
        }
      }
    }
  }, [showResponseForm, currentDate]);

  return (
    <div>
      <PeriodTypeContainer isActive={hasSelectedPeriod}>
        <RangeHeader>
          <ChangeDate onClick={changeDate('subtract')}>
            {languageDirection() === 'rtl' ? (
              <Icon type="chevron-right" className="right" />
            ) : (
              <Icon type="chevron-left" className="right" />
            )}
            {prevTitle}
          </ChangeDate>
          <span style={{ display: 'flex' }}>
            <ChangeDate onClick={() => changeDate('reset')()}>
              {convertDigitToProperFormat(superName)}
            </ChangeDate>
            <div style={{ display: 'flex' }}>
              {!showResponseForm && (
                <span
                  style={{
                    fontSize: '0.8rem',
                    marginTop: '3px',
                  }}
                >
                  <SelectWithPopover
                    options={periodTypes.map((e) => {
                      if (e.value === originalPeriodType)
                        return {
                          label: e.label + ' (' + t('defaultPeriodType') + ')',
                          value: e.value,
                        };
                      return e;
                    })}
                    isRequired={true}
                    allowRemove={false}
                    values={
                      periodType
                        ? [
                            {
                              value: periodType,
                              label: (messages as any)[periodType],
                            },
                          ]
                        : []
                    }
                    onChangeValues={(fieldOption) => {
                      if (fieldOption.length === 0)
                        setPeriodType(originalPeriodType);
                      setPeriodType(fieldOption[0].value as TimeScale);
                    }}
                    placeholder={t('periodType')}
                    label={t('periodType')}
                    target={
                      <ChangeDate>
                        <Icon
                          type="chevron-down"
                          color={
                            periodType !== originalPeriodType
                              ? `${red}`
                              : undefined
                          }
                        />
                      </ChangeDate>
                    }
                    placement={'left'}
                    arrow={false}
                  />
                </span>
              )}
            </div>
          </span>
          <ChangeDate onClick={changeDate('add')}>
            {nextTitle}
            {languageDirection() === 'rtl' ? (
              <Icon type="chevron-left" className="left" />
            ) : (
              <Icon type="chevron-right" className="left" />
            )}
          </ChangeDate>
        </RangeHeader>
        <Wrapper>
          {Object.keys(allPeriodTimes).map((key: string) => {
            const { start, middle, end, name } = allPeriodTimes[key];
            const findTime =
              periodMomentValues &&
              periodMomentValues.find(
                (statusKey: string) =>
                  start <= Number(statusKey) + 3600 * 1000 &&
                  Number(statusKey) <= end
              );
            if (isCurrentDateInTheRangeOfOffset(start, end, offset))
              return (
                <Periods
                  key={middle}
                  hide={hideCounts}
                  id={`${surveyItem?.id}-${start}_${end}_${middle}_${name}`}
                  count={
                    findTime !== undefined
                      ? findTime && periodCounts[Number(findTime)]
                      : 0
                  }
                >
                  <input
                    id={`${surveyItem?.id}-${middle}_${name}`}
                    name={periodType}
                    type="checkbox"
                    value={middle}
                    onChange={onChangeValues}
                    data-label={`${name} ${superName}`}
                    data-order={'-1'}
                    checked={
                      start <= Number(selectedPeriod) &&
                      Number(selectedPeriod) <= end
                    }
                  />
                  <PeriodLabel
                    isActive={!hasSelectedPeriod}
                    htmlFor={`${surveyItem?.id}-${middle}_${name}`}
                  >
                    {language === 'fa'
                      ? convertDigitToProperFormat(name)
                      : name}
                    {!hideCounts && <div></div>}
                  </PeriodLabel>
                </Periods>
              );
            return;
          })}
        </Wrapper>
      </PeriodTypeContainer>
    </div>
  );
};

export default PeriodTypeSelector;
